import { useEffect, useState } from 'react';
import type { StockSummaryRow, Paginated } from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

interface Named { id: string; code: string; name: string }

const fmtDate = (iso: string | null) => iso
  ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const daysUntil = (iso: string | null) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
};

export function Stock() {
  const { can } = useAuth();
  const [rows, setRows] = useState<StockSummaryRow[]>([]);
  const [branches, setBranches] = useState<Named[]>([]);
  const [branchId, setBranchId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const qs = branchId ? `?branchId=${branchId}` : '';
      setRows(await api.get<StockSummaryRow[]>(`/units/summary${qs}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat stok.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api.get<Paginated<Named>>('/branches?pageSize=100')
      .then((d) => setBranches(d.items))
      .catch(() => { /* branch scope may restrict this; not an error */ });
  }, []);
  useEffect(() => { void load(); }, [branchId]);

  const total = rows.reduce((a, r) => a + r.availableCount, 0);
  const expiringSoon = rows.reduce((a, r) => a + r.expiring3d, 0);
  const expiringWeek = rows.reduce((a, r) => a + r.expiring7d, 0);
  const quarantined = rows.reduce((a, r) => a + r.quarantinedCount, 0);

  return (
    <div className="stack">
      <div>
        <h2>Stok Darah</h2>
        <p className="muted" style={{ margin: '0.3rem 0 0', fontSize: 14 }}>
          Angka di sini adalah turunan dari kantong individual, bukan penghitung yang
          disimpan terpisah.
        </p>
      </div>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}

      <div className="grid-2">
        {[
          { label: 'Kantong tersedia', value: total, accent: false },
          { label: 'Kadaluarsa ≤ 3 hari', value: expiringSoon, accent: expiringSoon > 0 },
          { label: 'Kadaluarsa ≤ 7 hari', value: expiringWeek, accent: false },
          { label: 'Dikarantina', value: quarantined, accent: quarantined > 0 },
        ].map((k) => (
          <div key={k.label} className="card" style={{ padding: '0.9rem 1.1rem' }}>
            <span className="label">{k.label}</span>
            <div style={{
              fontSize: '1.8rem', fontWeight: 500, lineHeight: 1.2,
              color: k.accent ? 'var(--accent-strong)' : 'var(--foreground)',
            }}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      <div className="card stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="field" style={{ minWidth: 240 }}>
            <label className="label" htmlFor="s-branch">Cabang</label>
            <select id="s-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Semua cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
            </select>
          </div>
          {can('stock:write') && (
            <button
              onClick={async () => {
                setNotice(null); setError(null);
                try {
                  const r = await api.post<{ expired: number; reservedButExpired: number }>('/units/expire-sweep', {});
                  setNotice(
                    `${r.expired} kantong ditandai kadaluarsa.`
                    + (r.reservedButExpired > 0
                      ? ` ${r.reservedButExpired} kantong kadaluarsa masih terikat pesanan — perlu ditinjau manual.`
                      : ''),
                  );
                  await load();
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Gagal menjalankan sweep.');
                }
              }}
            >
              Sapu kadaluarsa
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? <p className="muted">Memuat…</p> : rows.length === 0 ? (
          <p className="muted">Belum ada kantong darah di stok.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Cabang</th><th>Komponen</th><th>Golongan</th>
                <th style={{ textAlign: 'right' }}>Tersedia</th>
                <th style={{ textAlign: 'right' }}>Dialokasikan</th>
                <th style={{ textAlign: 'right' }}>Karantina</th>
                <th>Kadaluarsa terdekat</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const remaining = daysUntil(r.earliestExpiry);
                const urgent = remaining !== null && remaining <= 3;
                return (
                  <tr key={`${r.branchId}-${r.componentTypeId}-${r.abo}-${r.rh}`}>
                    <td className="mono">{r.branchCode}</td>
                    <td className="mono">{r.componentCode}</td>
                    <td className="mono" style={{ fontWeight: 500 }}>
                      {r.abo}{r.rh === 'positive' ? '+' : '−'}
                    </td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 500 }}>{r.availableCount}</td>
                    <td className="mono muted" style={{ textAlign: 'right' }}>{r.reservedCount}</td>
                    <td className="mono" style={{ textAlign: 'right', color: r.quarantinedCount > 0 ? 'var(--accent-strong)' : 'var(--muted-foreground)' }}>
                      {r.quarantinedCount}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {fmtDate(r.earliestExpiry)}
                      {remaining !== null && (
                        <span className={urgent ? 'pill accent' : 'pill'} style={{ marginLeft: 6 }}>
                          {remaining <= 0 ? 'lewat' : `${remaining} hari`}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
